/**
 * ScriptEditPanel Component
 *
 * Composes FormatPresetBar, VoiceQuickPicker, and SectionEditor list
 * into the edit-mode content for ScriptPreviewModal.
 */

import { memo } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { SectionEditor } from './SectionEditor';
import { FormatPresetBar } from './FormatPresetBar';
import { VoiceQuickPicker } from './VoiceQuickPicker';
import type { EditableSection } from '@/hooks/useScriptEditState';

interface ScriptEditPanelProps {
  sections: EditableSection[];
  voiceConfig: { tts_provider: string; voice_id: string };
  resolution: string;
  language: string;
  onSectionsChange: (index: number, changes: Partial<EditableSection>) => void;
  onSectionDelete: (index: number) => void;
  onSectionMove: (index: number, direction: 'up' | 'down') => void;
  onAddSection: () => void;
  onVoiceProviderChange: (provider: string) => void;
  onVoiceChange: (voiceId: string) => void;
  onResolutionChange: (resolution: string) => void;
}

export const ScriptEditPanel = memo(function ScriptEditPanel({
  sections,
  voiceConfig,
  resolution,
  language,
  onSectionsChange,
  onSectionDelete,
  onSectionMove,
  onAddSection,
  onVoiceProviderChange,
  onVoiceChange,
  onResolutionChange,
}: ScriptEditPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      {/* Config bar: Format + Voice side by side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-secondary-700/20 rounded-xl">
        <FormatPresetBar value={resolution} onChange={onResolutionChange} />
        <VoiceQuickPicker
          ttsProvider={voiceConfig.tts_provider}
          voiceId={voiceConfig.voice_id}
          language={language}
          onProviderChange={onVoiceProviderChange}
          onVoiceChange={onVoiceChange}
        />
      </div>

      {/* Divider with sub-header */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-secondary-700/60" />
        <span className="text-[11px] font-medium text-secondary-500 uppercase tracking-wider">
          {t('scriptPreview.sections', { defaultValue: 'Sections' })}
        </span>
        <div className="h-px flex-1 bg-secondary-700/60" />
      </div>

      {/* Sections list */}
      <div className="space-y-2.5">
        {sections.length === 0 ? (
          <div className="text-center py-8 text-secondary-500 text-sm">
            {t('scriptPreview.noSections', {
              defaultValue: 'No sections yet. Add one to get started.',
            })}
          </div>
        ) : (
          sections.map((section, index) => (
            <SectionEditor
              key={`section-${index}`}
              section={section}
              index={index}
              onUpdate={onSectionsChange}
              onDelete={onSectionDelete}
              onMoveUp={(i) => onSectionMove(i, 'up')}
              onMoveDown={(i) => onSectionMove(i, 'down')}
              isFirst={index === 0}
              isLast={index === sections.length - 1}
              canDelete={sections.length > 1}
            />
          ))
        )}
      </div>

      {/* Add section button */}
      <button
        type="button"
        onClick={onAddSection}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-dashed border-secondary-700/60 text-secondary-500 hover:text-secondary-300 hover:border-secondary-500 hover:bg-secondary-700/20 transition-all duration-200 text-xs min-h-[40px]"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('scriptPreview.addSection', { defaultValue: 'Add Section' })}
      </button>
    </div>
  );
});
